interface CTAConfig {
  calculatorType: string;
  basicQuestion: string;
  aiResponse: {
    summary: string;
    formula?: string;
    calculation?: string;
    insights: string[];
    conclusion: string;
  };
  ctaText?: string;
}

type CalculatorCTAs = {
  [key: string]: CTAConfig;
};

export const calculatorCTAs: CalculatorCTAs = {
  // Health & Lifestyle
  "bmi": {
    calculatorType: "BMI Calculator",
    basicQuestion: "I'm 5'9\" and 180 lbs with a BMI of 26.6. What does this mean for my health goals and how should I adjust my lifestyle?",
    aiResponse: {
      summary: "Your BMI of 26.6 puts you in the 'overweight' category, but BMI alone doesn't tell the whole story. Let me provide a personalized analysis.",
      formula: "BMI = \\frac{weight(kg)}{height(m)^2} = \\frac{81.6}{(1.75)^2} = 26.6",
      insights: [
        "Consider body composition: muscle mass can elevate BMI without health risks",
        "Target BMI of 24 means losing ~15 lbs, but focus on 1-2 lbs per week for sustainability",
        "Combine 300-500 calorie deficit with strength training to preserve muscle mass",
        "Track waist circumference (target <40\" for men, <35\" for women) as an additional health marker"
      ],
      conclusion: "Aim for gradual weight loss through a balanced 2,000 calorie diet with 30+ minutes of daily activity. Consider consulting a healthcare provider for personalized guidance."
    }
  },

  "tdee": {
    calculatorType: "TDEE Calculator",
    basicQuestion: "My TDEE is 2,400 calories. How should I structure my meals if I want to lose 1 lb per week while maintaining muscle?",
    aiResponse: {
      summary: "To lose 1 lb per week, you need a 500 calorie daily deficit. Here's your optimized meal structure for muscle preservation.",
      calculation: "Target: 2,400 - 500 = 1,900 calories/day\nProtein: 180g (40%), Carbs: 190g (40%), Fat: 42g (20%)",
      insights: [
        "Prioritize 1g protein per lb bodyweight to maintain muscle during deficit",
        "Time 40% of carbs around workouts for energy and recovery",
        "Don't go below 1,700 calories - metabolic adaptation can stall progress",
        "Consider diet breaks every 8-12 weeks to reset hormones and prevent plateaus"
      ],
      conclusion: "Eat 1,900 calories daily with high protein (180g), moderate carbs around training, and track weekly averages instead of daily fluctuations."
    }
  },

  "body-fat": {
    calculatorType: "Body Fat Calculator",
    basicQuestion: "My body fat is 22%. How does this compare to athletes, and what's a realistic goal for me?",
    aiResponse: {
      summary: "At 22% body fat, you're in the 'fitness' category. Let me show you realistic targets and how to get there.",
      insights: [
        "Athletes typically range 6-13% (men) or 14-20% (women), but this requires extreme dedication",
        "15-17% is a sustainable 'lean and healthy' goal that shows ab definition",
        "Losing 1% body fat per month is aggressive but achievable with proper nutrition and training",
        "Stress, sleep, and hormone balance significantly impact body composition progress"
      ],
      conclusion: "Aim for 15-17% body fat over 5-7 months through a 300-500 calorie deficit, strength training 3-4x/week, and adequate sleep. This balances aesthetics with sustainability."
    }
  },

  "bmr": {
    calculatorType: "BMR Calculator",
    basicQuestion: "My BMR is 1,650 calories. If I'm sedentary now but want to start exercising, how should I adjust my intake?",
    aiResponse: {
      summary: "Your BMR of 1,650 is your baseline. Let's create a strategic plan to add exercise without stalling progress.",
      calculation: "Current TDEE: 1,650 × 1.2 = 1,980 cal/day\nWith 3x/week exercise: 1,650 × 1.375 = 2,269 cal/day\nIncrease: +289 calories",
      insights: [
        "Start exercise gradually: add 100-150 calories per week as activity increases",
        "Don't eat back all exercise calories immediately - your body adapts over 2-3 weeks",
        "Monitor weight trend over 2-week periods, not daily fluctuations",
        "If weight loss stalls, add activity before cutting more calories"
      ],
      conclusion: "Begin with 2,100 calories, increase to 2,200-2,300 as you build a consistent exercise routine, and adjust based on 2-week averages."
    }
  },

  // Finance
  "compound-interest": {
    calculatorType: "Compound Interest Calculator",
    basicQuestion: "I'll have $12,833 in 5 years with my current savings. But what if I add $200/month? What if rates change? How do I optimize this?",
    aiResponse: {
      summary: "Let me show you how small monthly contributions create exponential growth and how to protect against rate changes.",
      formula: "FV = P(1 + r)^n + PMT \\times \\frac{(1 + r)^n - 1}{r}",
      calculation: "Base: $10,000 @ 5% = $12,833\nWith $200/mo: $10,000 + $12,000 contributions + $2,158 interest = $24,158\nDifference: $11,325 extra from just $200/month!",
      insights: [
        "Monthly contributions have outsized impact: $200/mo adds $11,325 vs. $2,833 from initial $10k",
        "If rates drop to 3%, your final value decreases to $22,567 - diversification protects you",
        "Consider high-yield savings (5%+) for emergency funds, index funds for long-term growth",
        "Automate contributions to remove willpower - 'pay yourself first' principle"
      ],
      conclusion: "Add $200/month to reach $24,158 instead of $12,833. Diversify across savings accounts (liquidity) and index funds (growth) to balance rate risk."
    }
  },

  "loan-payment": {
    calculatorType: "Loan Payment Calculator",
    basicQuestion: "My monthly payment is $387 on a $20,000 loan at 6%. Should I pay extra principal, or invest that money elsewhere?",
    aiResponse: {
      summary: "This is a guaranteed 6% return vs. potential market returns. Let me break down the math and psychology.",
      calculation: "Extra $100/mo toward principal:\n- Saves $1,847 in interest\n- Pays off 14 months early\n\nInvesting $100/mo at 8% return:\n- Grows to $7,347 in 5 years\n- But market risk and taxes apply",
      insights: [
        "6% guaranteed return (debt payoff) vs. ~8-10% average market return with volatility",
        "Consider your risk tolerance: debt payoff = peace of mind, investing = wealth building",
        "Hybrid approach: pay minimum + invest difference works if market returns exceed loan rate",
        "If loan is 6%+ or you're risk-averse, prioritize payoff. If 4% or less, prioritize investing"
      ],
      conclusion: "At 6% interest, consider a hybrid: put $50 extra to principal (guaranteed return) and $50 to investments (growth potential). This balances debt freedom with wealth building."
    }
  },

  "mortgage": {
    calculatorType: "Mortgage Calculator",
    basicQuestion: "My monthly mortgage is $1,624. Should I pay biweekly, or refinance if rates drop 0.5%?",
    aiResponse: {
      summary: "Let's analyze biweekly payments vs. refinancing to find your optimal strategy.",
      calculation: "Biweekly payments ($812 every 2 weeks):\n- Extra payment per year: $1,624\n- Saves: $34,328 in interest\n- Pays off: 4.5 years early\n\nRefinancing (6% → 5.5%):\n- New payment: $1,534 (-$90/mo)\n- Closing costs: ~$4,000\n- Break-even: 44 months",
      insights: [
        "Biweekly payments are free and painless - you naturally make 26 half-payments per year",
        "Refinancing only makes sense if you'll stay in the home past break-even (3-4 years)",
        "Rates need to drop 0.75-1% to justify refinance costs in most cases",
        "Consider both: refinance then switch to biweekly for maximum savings"
      ],
      conclusion: "Start biweekly payments now (free, immediate savings). If rates drop 0.75%+, refinance and continue biweekly on the new loan for maximum impact."
    }
  },

  "roi": {
    calculatorType: "ROI Calculator",
    basicQuestion: "My investment ROI is 15%. Is this good? How does it compare to the market? Should I sell or hold?",
    aiResponse: {
      summary: "15% ROI is solid, but context matters. Let's compare to benchmarks and determine your next move.",
      formula: "ROI = \\frac{Gain - Cost}{Cost} \\times 100\\% = 15\\%",
      insights: [
        "S&P 500 averages ~10% annually - you're outperforming by 5 percentage points",
        "Consider time frame: 15% in 1 year is great, 15% over 5 years underperforms (50% cumulative)",
        "Risk-adjusted returns matter: did you take 2x volatility to earn that 5% premium?",
        "Tax implications: selling triggers capital gains (0-20% federal + state taxes)"
      ],
      conclusion: "If this is a 1-year return, you're outperforming. Hold unless you need funds or fundamentals changed. If 5-year return, consider rebalancing to index funds for diversification."
    }
  },

  // Physics
  "projectile-motion": {
    calculatorType: "Projectile Motion Calculator",
    basicQuestion: "The ball travels 40.8m in ideal conditions. But what if there's wind? What angle gives maximum distance with air resistance?",
    aiResponse: {
      summary: "In vacuum, 45° is optimal. But real-world conditions change everything. Let me show you the adjustments.",
      formula: "R = \\frac{v_0^2 \\sin(2\\theta)}{g} \\quad \\text{(no air resistance)}",
      calculation: "Ideal (45°): 40.8m\nWith air resistance: ~35-37m (10-15% reduction)\nOptimal angle with drag: 38-42° (depends on velocity and ball size)",
      insights: [
        "Air resistance favors lower angles: 38-42° often beats 45° in practice",
        "Headwind: reduce angle by 3-5° and increase initial velocity if possible",
        "Tailwind: increase angle by 2-4° to maximize time in air and benefit from wind",
        "Ball size matters: larger cross-section = more drag, favor lower angles"
      ],
      conclusion: "For maximum real-world distance, use 40° with a headwind, 42° with no wind, or 44° with a tailwind. Air resistance typically reduces distance by 10-15%."
    }
  },

  "force": {
    calculatorType: "Force Calculator",
    basicQuestion: "I calculated 100N of force, but how does friction affect this? What if the surface is wet or icy?",
    aiResponse: {
      summary: "Your 100N applied force is modified by friction coefficients. Let's see how different surfaces change everything.",
      formula: "F_{net} = F_{applied} - F_{friction} = F - \\mu N",
      calculation: "Dry concrete (μ = 0.7): F_net = 100N - 70N = 30N\nWet surface (μ = 0.5): F_net = 100N - 50N = 50N\nIce (μ = 0.1): F_net = 100N - 10N = 90N",
      insights: [
        "Friction reduces effective force by 30-70% depending on surface",
        "Wet surfaces can increase force efficiency by 40% vs. dry (0.5 vs. 0.7 coefficient)",
        "Ice requires 7x less force to overcome friction - danger and efficiency",
        "Static friction (starting) is higher than kinetic friction (moving) - overcome inertia first"
      ],
      conclusion: "On dry concrete, only 30N of your 100N goes to motion. On ice, 90N is effective. Choose surfaces strategically for efficiency or safety."
    }
  },

  "acceleration": {
    calculatorType: "Acceleration Calculator",
    basicQuestion: "My car accelerates at 3.5 m/s². How does this compare to other vehicles? What 0-60 time does this predict?",
    aiResponse: {
      summary: "3.5 m/s² acceleration is moderate. Let me benchmark it and predict your 0-60 mph time.",
      formula: "v = at \\quad \\Rightarrow \\quad t = \\frac{v}{a} = \\frac{26.8 m/s}{3.5 m/s^2} = 7.7 \\text{ seconds}",
      calculation: "Your car: 0-60 mph in ~7.7 seconds\nAverage sedan: 7-9 seconds\nSports car: 3-5 seconds\nSupercar: <3 seconds",
      insights: [
        "You're slightly above average - most economy cars do 0-60 in 8-10 seconds",
        "Acceleration decreases at higher speeds due to air resistance (not captured in basic formula)",
        "Real 0-60 times are 10-15% slower than calculated due to friction, gear changes, and traction limits",
        "Doubling acceleration doesn't double cost - it often increases exponentially"
      ],
      conclusion: "Your 3.5 m/s² acceleration gives ~7.7 second 0-60 time, making this a peppy mid-range vehicle. Sports cars achieve 5-8 m/s² acceleration."
    }
  },

  // Chemistry
  "molarity": {
    calculatorType: "Molarity Calculator",
    basicQuestion: "I have a 0.5 M NaOH solution. How do I dilute this safely for my experiment? What safety precautions matter?",
    aiResponse: {
      summary: "0.5 M NaOH is corrosive. Let me guide you through safe dilution procedure and critical safety steps.",
      formula: "M_1V_1 = M_2V_2 \\quad \\Rightarrow \\quad V_1 = \\frac{M_2 V_2}{M_1} = \\frac{0.1M \\times 1L}{0.5M} = 0.2L",
      calculation: "To make 1L of 0.1M solution:\n- Take 200mL of 0.5M stock\n- Add to ~700mL water\n- Mix, then add water to exactly 1L",
      insights: [
        "ALWAYS add base to water, never water to base (exothermic reaction can cause splashing)",
        "Use cold water to minimize heat generation during dilution",
        "Wear goggles, gloves, and lab coat - 0.5M NaOH causes chemical burns",
        "Prepare neutralizing agent (weak acid like vinegar) before starting in case of spills"
      ],
      conclusion: "Add 200mL of stock 0.5M NaOH to 700mL water slowly with stirring, then top to 1L. Work in a fume hood with PPE, and never rush the process."
    }
  },

  "ph": {
    calculatorType: "pH Calculator",
    basicQuestion: "My solution has pH 3.5. How much more acidic is this than pH 5? What does this mean for my reaction?",
    aiResponse: {
      summary: "pH is logarithmic - small number changes mean huge concentration differences. Let me show you.",
      formula: "pH = -\\log[H^+] \\quad \\Rightarrow \\quad [H^+] = 10^{-pH}",
      calculation: "pH 3.5: [H⁺] = 10⁻³·⁵ = 3.16 × 10⁻⁴ M\npH 5.0: [H⁺] = 10⁻⁵ = 1.00 × 10⁻⁵ M\nDifference: 31.6x more acidic!",
      insights: [
        "Each pH unit is 10x difference in H⁺ concentration - 1.5 unit drop = 31.6x increase",
        "pH 3.5 is mildly acidic (lemon juice ~2.0, vinegar ~2.5, soda ~2.5-4.0)",
        "Reaction rates often depend on [H⁺], so 31x concentration can mean 31x faster reaction",
        "Buffer capacity matters: solutions resist pH change near their pKa value"
      ],
      conclusion: "Your pH 3.5 solution is 31.6x more acidic than pH 5. This significantly accelerates acid-catalyzed reactions and requires careful handling."
    }
  },

  "stoichiometry": {
    calculatorType: "Stoichiometry Calculator",
    basicQuestion: "I need 2 moles of product. How much reactant do I need, and how do I account for reaction efficiency?",
    aiResponse: {
      summary: "Stoichiometry gives theoretical amounts, but real reactions have yields <100%. Let me show you practical calculations.",
      formula: "\\text{moles reactant} = \\text{moles product} \\times \\frac{\\text{coef. reactant}}{\\text{coef. product}}",
      calculation: "Theoretical: 2 moles product × (1 mole reactant / 1 mole product) = 2 moles\nWith 75% yield: 2 / 0.75 = 2.67 moles needed\nWith 90% yield: 2 / 0.90 = 2.22 moles needed",
      insights: [
        "Lab reactions rarely achieve 100% yield - 70-90% is typical for well-known procedures",
        "Account for losses during transfer, purification, and incomplete reaction",
        "Add 10-20% excess reactant as insurance, especially for expensive products",
        "Track actual yields over time to refine future calculations for your specific setup"
      ],
      conclusion: "For 2 moles product at 80% typical yield, start with 2.5 moles reactant (25% excess). Adjust based on your lab's historical performance."
    }
  },

  // Health (Additional)
  "ideal-weight": {
    calculatorType: "Ideal Weight Calculator",
    basicQuestion: "My ideal weight is 70 kg. But considering my body frame and muscle mass goals, what's the healthiest target for me?",
    aiResponse: {
      summary: "Ideal weight formulas provide a starting point, but your actual healthy weight depends on body composition, frame size, and fitness goals.",
      calculation: "Ideal Weight: 70 kg (Medium Frame)\nSmall Frame: 63-67 kg\nLarge Frame: 74-78 kg\nAthlete Range: 68-75 kg (with higher muscle mass)",
      insights: [
        "Body frame matters: small frames target 10% lower, large frames 10% higher than formula result",
        "Muscle mass increases healthy weight: athletes can weigh 5-10 kg more than 'ideal' while being healthier",
        "Focus on body composition (muscle vs. fat) rather than just scale weight",
        "Waist-to-hip ratio and body fat percentage are better health indicators than weight alone"
      ],
      conclusion: "Target 68-75 kg depending on frame and muscle goals. Prioritize strength training and body composition over hitting a specific number."
    }
  },

  "percentage": {
    calculatorType: "Percentage Calculator",
    basicQuestion: "I calculated 25% of 200 is 50. But how do I use this to analyze profit margins and determine if my business is healthy?",
    aiResponse: {
      summary: "Percentages are fundamental to business analysis. Let me show you how to interpret margins, growth rates, and financial health.",
      formula: "Percentage = \\frac{Value}{Total} \\times 100\\%",
      calculation: "25% of 200 = 50\nProfit Margin: 25% is strong (>20% is excellent)\nGrowth Rate: 25% YoY is aggressive expansion\nMarkdown Impact: 25% discount requires 33% more sales to maintain revenue",
      insights: [
        "Profit margins vary by industry: 25% is excellent for retail, average for SaaS, low for consulting",
        "Percentage changes aren't symmetrical: 25% gain requires 20% loss to return to start",
        "Compound percentages multiply: two 10% increases = 21% total (not 20%)",
        "Always identify the base: '25% more than X' vs. '25% of X' yield different results"
      ],
      conclusion: "Use percentages to compare ratios across time and between companies. A 25% margin suggests healthy profitability if consistent."
    }
  },

  "discount": {
    calculatorType: "Discount Calculator",
    basicQuestion: "A 30% discount saves me $45. Should I buy now, or wait for a better sale? What's the psychology at play?",
    aiResponse: {
      summary: "Discounts create urgency through perceived savings. Let me show you the math and psychology behind pricing strategies.",
      calculation: "Original: $150\n30% off: -$45\nFinal Price: $105\n\nOriginal Markup Analysis:\nIf item cost store $60 to buy:\n$150 = 150% markup\n$105 = 75% markup (still profitable!)",
      insights: [
        "Retailers often mark up 100-300% initially, so 30-50% discounts still yield profit",
        "Anchor pricing makes $105 feel cheap because you saw $150 first (psychological trick)",
        "Waiting risks stockout but may yield 40-60% off during end-of-season clearance",
        "Consider cost-per-use: $105 item used 50 times = $2.10/use vs. $80 item used 5 times = $16/use"
      ],
      conclusion: "At 30% off, buy if you need it within 3 months. Wait for 40-50% off if it's purely discretionary and you can risk missing out."
    }
  },

  // Math
  "quadratic": {
    calculatorType: "Quadratic Equation Solver",
    basicQuestion: "The roots are x = 2 and x = 3. But what does this mean for my optimization problem? Where's the maximum?",
    aiResponse: {
      summary: "Roots tell you where the parabola crosses zero, but the vertex (max/min) is between them. Let me find it.",
      formula: "x_{vertex} = \\frac{x_1 + x_2}{2} = \\frac{2 + 3}{2} = 2.5",
      calculation: "Vertex x-coordinate: 2.5 (midpoint of roots)\nIf a < 0: maximum at x = 2.5\nIf a > 0: minimum at x = 2.5",
      insights: [
        "Parabola vertex always lies exactly halfway between the two roots",
        "If your coefficient a is negative, you have a maximum (profit, height, etc.)",
        "If a is positive, you have a minimum (cost, distance, error)",
        "Plug x = 2.5 back into original equation to find the actual max/min value"
      ],
      conclusion: "Your optimization point is at x = 2.5 (between roots 2 and 3). This is a maximum if parabola opens downward, minimum if upward."
    }
  },

  "standard-deviation": {
    calculatorType: "Standard Deviation Calculator",
    basicQuestion: "My standard deviation is 12.5. Is this high? How many data points fall within 1 standard deviation?",
    aiResponse: {
      summary: "Standard deviation measures spread - but context matters. Let me interpret this using the empirical rule.",
      formula: "\\sigma = \\sqrt{\\frac{\\sum(x_i - \\mu)^2}{n}} = 12.5",
      calculation: "If mean = 50, then:\n±1σ range: 37.5 to 62.5 (68% of data)\n±2σ range: 25 to 75 (95% of data)\n±3σ range: 12.5 to 87.5 (99.7% of data)",
      insights: [
        "68-95-99.7 rule: 68% of data within 1σ, 95% within 2σ, 99.7% within 3σ",
        "σ = 12.5 is 'high' if mean = 50 (25% relative), but 'low' if mean = 500 (2.5% relative)",
        "Use coefficient of variation (σ/mean × 100%) to compare across different scales",
        "Values beyond 2σ are unusual, beyond 3σ are rare outliers"
      ],
      conclusion: "68% of your data falls within ±12.5 of the mean. Whether this is 'high' depends on your mean - calculate CV = (12.5/mean) × 100% for relative spread."
    }
  },

  "matrix": {
    calculatorType: "Matrix Calculator",
    basicQuestion: "I multiplied two matrices and got a result. How do I know if this is invertible, and what does that mean for solving systems?",
    aiResponse: {
      summary: "Matrix invertibility determines if your system has a unique solution. Let me show you how to check.",
      formula: "A^{-1} \\text{ exists if } \\det(A) \\neq 0",
      insights: [
        "Determinant = 0 means matrix is singular (not invertible) - system has no solution or infinite solutions",
        "Determinant ≠ 0 means unique solution exists: x = A⁻¹b",
        "For 2×2 matrix: det = ad - bc. If this equals zero, rows are linearly dependent",
        "Numerically, det very close to 0 (like 0.0001) indicates ill-conditioned matrix - solutions are unstable"
      ],
      conclusion: "Calculate the determinant of your result matrix. If det ≠ 0, it's invertible and your system has exactly one solution. If det = 0, check for dependencies."
    }
  },

  // Health & Lifestyle (Batch 2)
  "macro-split": {
    calculatorType: "Macro Split Calculator",
    basicQuestion: "I have 2000 calories to work with. Should I do 40/30/30 or 35/45/20 for muscle gain? What's the real difference?",
    aiResponse: {
      summary: "Macro splits matter less than hitting total protein and calories consistently. Let me compare these strategies.",
      calculation: "40/30/30: 200g protein, 150g carbs, 67g fat\n35/45/20: 175g protein, 225g carbs, 44g fat\nDifference: -25g protein, +75g carbs, -23g fat",
      insights: [
        "Minimum protein: 0.8-1g per lb bodyweight for muscle gain - both options likely sufficient",
        "Extra carbs (45%) fuel harder training = more muscle stimulus despite slightly less protein",
        "Fats below 20% can affect hormone production - 40/30/30 provides better hormonal support",
        "Consistency beats optimization: stick with whichever split you can maintain 90% of the time"
      ],
      conclusion: "Choose 35/45/20 if you train intensely 5+ days/week (carbs fuel performance). Choose 40/30/30 for 3-4 day programs (better recovery via fats). Track for 4 weeks and adjust."
    }
  },

  "running-pace": {
    calculatorType: "Running Pace Calculator",
    basicQuestion: "My 5K pace is 6:30/mile. What pace should I use for a marathon to avoid hitting the wall?",
    aiResponse: {
      summary: "Marathon pace should be ~45-60 seconds/mile slower than your 5K pace to account for endurance factors. Let me calculate your zones.",
      calculation: "5K Pace: 6:30/mile\nRecommended Marathon Pace: 7:15-7:30/mile\n10K equivalent: 6:45/mile\nHalf Marathon: 7:00/mile",
      insights: [
        "5K uses 95% aerobic + 5% anaerobic; marathon is 99% aerobic - you must slow down significantly",
        "Running first half at marathon pace feels 'too easy' but prevents glycogen depletion at mile 20",
        "Negative splits (second half faster) improve finish times by 2-5% vs. even splits",
        "Add 15-20 seconds per mile in hot weather (>70°F) to avoid heat-related performance drops"
      ],
      conclusion: "Target 7:15-7:20/mile for your first marathon. Run first 13.1 miles at 7:20, then speed up if you feel strong. This prevents 'hitting the wall' at mile 20."
    }
  },

  "age": {
    calculatorType: "Age Calculator",
    basicQuestion: "I'm 10,950 days old. What life stage am I in, and what should I prioritize health-wise?",
    aiResponse: {
      summary: "You're approximately 30 years old - a critical period for establishing lifelong health habits. Let me break down age-specific priorities.",
      calculation: "10,950 days = 30 years old\nDecades lived: 3\nDecades to retirement (65): 3.5\nEstimated life expectancy: ~48-50 more years",
      insights: [
        "Age 25-35: peak earning potential begins, but health habits set trajectory for decades",
        "Metabolism slows ~2% per decade after 25 - you burn 200-300 fewer calories than at age 20",
        "Muscle mass decreases 3-5% per decade after 30 without resistance training",
        "Preventive care now (exercise, diet, sleep) has exponential returns vs. reactive care later"
      ],
      conclusion: "At 30, prioritize: strength training 3x/week to preserve muscle, establish consistent sleep (7-8 hours), and get baseline health screening (cholesterol, blood pressure, A1C)."
    }
  },

  // Loans & Tax (Batch 2)
  "loan-amortization": {
    calculatorType: "Loan Amortization Calculator",
    basicQuestion: "I see my payment schedule. Why do my early payments go mostly to interest? Should I refinance or pay extra principal?",
    aiResponse: {
      summary: "Amortization front-loads interest because balance is highest early on. Let me show you optimization strategies.",
      calculation: "Month 1: $387 payment = $100 interest + $287 principal (balance $19,713)\nMonth 60: $387 payment = $2 interest + $385 principal (balance $385)\nTotal Interest Paid: $3,220 over life of loan",
      insights: [
        "Interest = Balance × Monthly Rate, so early payments see higher interest on higher balance",
        "Extra $100/month toward principal saves $1,200 in interest and shortens loan by 12 months",
        "Refinancing makes sense if new rate is 1%+ lower and you'll keep loan 3+ years (break-even)",
        "Biweekly payments (half-payment every 2 weeks) = 1 extra payment/year with minimal effort"
      ],
      conclusion: "If rate >6%, pay extra $50-100/month toward principal. If rate drops 1%, refinance. Consider biweekly payments for automatic acceleration."
    }
  },

  "loan-repayment": {
    calculatorType: "Loan Repayment Calculator",
    basicQuestion: "Should I pay off my $10,000 student loan at 4.5% or invest that money in the market?",
    aiResponse: {
      summary: "This is the classic 'guaranteed 4.5% return vs. potential 8-10% market return' debate. Let me show you both scenarios.",
      calculation: "Pay Off Loan: Save $2,285 interest over 10 years (guaranteed)\nInvest $100/mo at 8%: Grow to $18,295 after 10 years\nNet Difference: $18,295 - $12,285 = $6,010 more from investing",
      insights: [
        "4.5% loan rate is below historical market returns (8-10%), favoring investing",
        "Psychological benefit of debt freedom has real value - stress reduction improves decisions",
        "Tax deduction on student loan interest (up to $2,500) reduces effective rate to ~3.4%",
        "Hybrid approach: pay minimum + invest difference balances growth with security"
      ],
      conclusion: "At 4.5%, lean toward investing while paying minimum. If rate were 7%+, prioritize payoff. Consider 60/40 split: 60% to investing, 40% extra to principal."
    }
  },

  "sales-tax": {
    calculatorType: "Sales Tax Calculator",
    basicQuestion: "I'm paying $65 in tax on a $1,000 purchase. Can I avoid this by buying online or in another state?",
    aiResponse: {
      summary: "Sales tax varies by state (0-10%), and online purchases have specific rules post-2018 Supreme Court ruling. Let me explain the strategy.",
      calculation: "Your Rate: 6.5%\nTax Paid: $65\nNo-tax state (OR, NH, MT, DE, AK): $0 tax\nHigh-tax state (CA): $95-100 tax\nSavings potential: $65 by shopping in tax-free state",
      insights: [
        "5 states have no sales tax: Oregon, New Hampshire, Montana, Delaware, Alaska",
        "Online purchases now charge sales tax for your shipping address (not seller location)",
        "Use tax applies: if you avoid sales tax, you legally owe equivalent 'use tax' when filing taxes",
        "Big purchases (car, furniture) can justify driving to no-tax state if you live nearby"
      ],
      conclusion: "You can't legally avoid this tax online (charged based on your address). If you live near OR/NH/MT/DE/AK border, large purchases justify the trip. Otherwise, accept the 6.5%."
    }
  },

  // Physics - Optics (Batch 2)
  "lens-equation": {
    calculatorType: "Lens Equation Calculator",
    basicQuestion: "My focal length is 50mm. What object distance gives me the sharpest portrait photo with blurred background?",
    aiResponse: {
      summary: "Portrait background blur (bokeh) depends on focal length, aperture, and subject distance. Let me optimize for maximum blur.",
      formula: "\\frac{1}{f} = \\frac{1}{d_o} + \\frac{1}{d_i} \\quad \\text{and} \\quad \\text{DoF} \\propto \\frac{f^2}{N \\cdot d_o}",
      calculation: "50mm lens at f/1.8:\nOptimal subject distance: 2-3 meters (6-10 feet)\nDepth of field: ~5cm (razor thin focus)\nBackground blur: Maximum at 2m with f/1.8",
      insights: [
        "Shorter distance = more background blur, but too close causes distortion (<1.5m)",
        "Sweet spot for portraits: 2-3m with 50mm lens (natural perspective + strong bokeh)",
        "Wider aperture (f/1.4 vs. f/2.8) doubles blur effect but reduces depth of field",
        "85mm or 105mm lens at same distance gives even stronger background separation"
      ],
      conclusion: "Position subject 2-2.5 meters away, use f/1.8-2.0 aperture, and ensure background is 3+ meters behind subject for maximum blur with natural perspective."
    }
  },

  "mirror-equation": {
    calculatorType: "Mirror Equation Calculator",
    basicQuestion: "I have a concave mirror with radius 30cm. Where should I place my object to get a magnified, upright image?",
    aiResponse: {
      summary: "Concave mirrors produce upright, magnified images only when object is inside the focal point. Let me show you the setup.",
      formula: "f = \\frac{R}{2} = 15\\text{cm} \\quad \\text{and} \\quad M = -\\frac{d_i}{d_o}",
      calculation: "Focal length: 15cm\nFor magnified, upright image: place object at 10cm (inside f)\nImage distance: -30cm (virtual, behind mirror)\nMagnification: 3× (image is 3 times larger)",
      insights: [
        "Object inside focal point = virtual, upright, magnified image (like makeup/shaving mirrors)",
        "Object outside focal point = real, inverted image (not what you want for magnification)",
        "Closer to mirror (but < f) = larger magnification (but harder to see full image)",
        "At focal point exactly = no image formed (rays are parallel)"
      ],
      conclusion: "Place object 10-12cm from mirror (inside the 15cm focal point) for 2-3× magnification with upright orientation. This is how magnifying mirrors work."
    }
  },

  "snells-law": {
    calculatorType: "Snell's Law Calculator",
    basicQuestion: "Light bends when entering water. At what angle does total internal reflection occur for a fiber optic cable?",
    aiResponse: {
      summary: "Total internal reflection happens at the critical angle, enabling fiber optics to transmit light. Let me calculate this threshold.",
      formula: "n_1 \\sin(\\theta_c) = n_2 \\quad \\Rightarrow \\quad \\theta_c = \\sin^{-1}\\left(\\frac{n_2}{n_1}\\right)",
      calculation: "Glass (n=1.5) to air (n=1.0):\nCritical angle: sin⁻¹(1.0/1.5) = 41.8°\nAngles > 41.8° = total internal reflection (100% reflected)\nAngles < 41.8° = partial transmission (light escapes)",
      insights: [
        "Fiber optics use this principle: light hits at >42° and bounces perfectly without loss",
        "Water to air: critical angle = 48.6° (why underwater objects appear at different angles)",
        "Diamond to air: critical angle = 24.4° (why diamonds sparkle - light trapped inside)",
        "No critical angle when going from low to high refractive index (air to glass)"
      ],
      conclusion: "For fiber optic cable (glass/plastic), maintain light entry angle >42° to ensure total internal reflection and zero signal loss over long distances."
    }
  },

  // Physics - Electricity (Batch 2)
  "ohms-law": {
    calculatorType: "Ohm's Law Calculator",
    basicQuestion: "My circuit needs 12V and 2A. What wattage resistor do I need, and how do I prevent it from overheating?",
    aiResponse: {
      summary: "You need a 6Ω resistor, but wattage rating matters for heat dissipation. Let me calculate safe specifications.",
      formula: "R = \\frac{V}{I} = 6\\Omega \\quad \\text{and} \\quad P = VI = 24W",
      calculation: "Resistance: 6Ω\nPower dissipated: 24W (significant heat!)\nSafe resistor rating: 30-40W (25% overhead)\nAlternative: Use 3× 18Ω (2W each) in parallel = 6Ω total, 8W per resistor",
      insights: [
        "24W resistor will get HOT (~150-200°C) - needs heatsink or larger wattage rating",
        "Parallel resistor trick: distribute heat across multiple components for same total resistance",
        "Derating rule: use resistor rated 25-50% above calculated power for reliability",
        "Consider voltage regulator IC instead of resistor for better efficiency (less heat waste)"
      ],
      conclusion: "Use a 40W resistor with heatsink, or better: use 3× 18Ω 10W resistors in parallel (distributes heat). For production, switch to voltage regulator IC for 95% efficiency."
    }
  },

  "circuit-power": {
    calculatorType: "Circuit Power Calculator",
    basicQuestion: "My circuit draws 120W. How much does this cost per month at $0.12/kWh if it runs 24/7?",
    aiResponse: {
      summary: "Always-on devices have hidden costs. Let me calculate annual energy consumption and compare to alternatives.",
      calculation: "120W × 24 hours × 30 days = 86.4 kWh/month\nCost: 86.4 × $0.12 = $10.37/month\nAnnual cost: $124.44/year\nLifetime cost (10 years): $1,244.40",
      insights: [
        "Small always-on loads compound: 120W device costs $1,244 over decade vs. $25 device cost",
        "Vampire power (standby): many devices draw 5-20W even when 'off' - add smart plugs for control",
        "LED upgrade example: 120W incandescent → 18W LED saves $102/year per bulb (10-month payback)",
        "Time-of-use rates: running at night (off-peak) can save 30-50% on electricity costs"
      ],
      conclusion: "This 120W device costs $124/year to run continuously. Add a timer/smart plug to run only when needed, or upgrade to lower-wattage alternative if available."
    }
  },

  "electric-field": {
    calculatorType: "Electric Field Calculator",
    basicQuestion: "I calculated an electric field of 5000 N/C. Is this safe for humans, or does it pose a risk?",
    aiResponse: {
      summary: "Electric field strength determines safety. Let me compare your value to safety thresholds and natural phenomena.",
      formula: "E = \\frac{F}{q} = 5000 \\, \\text{N/C} = 5 \\, \\text{kV/m}",
      calculation: "Your field: 5 kV/m\nSafe exposure limit: <5 kV/m (prolonged exposure)\nBreakdown of air: ~3000 kV/m (lightning begins)\nHigh-voltage power lines: 1-10 kV/m directly underneath",
      insights: [
        "5 kV/m is at safety threshold - brief exposure OK, prolonged exposure requires shielding",
        "Corona discharge (sparking) begins around 30 kV/m in air - your field is safe from arcing",
        "Power lines create 5-10 kV/m underneath but field drops with distance² law",
        "Biological effects are controversial: WHO says <5 kV/m safe, some studies suggest caution"
      ],
      conclusion: "5 kV/m is at the safety threshold. Reduce field strength below 5 kV/m for prolonged human exposure, or maintain safe distance using inverse-square law (double distance = 1/4 field)."
    }
  },

  "coulombs-law": {
    calculatorType: "Coulomb's Law Calculator",
    basicQuestion: "Two charges repel with 0.5 N force. If I double the distance, what happens to the force and why?",
    aiResponse: {
      summary: "Coulomb's Law follows inverse-square relationship - doubling distance reduces force to 1/4. Let me demonstrate this principle.",
      formula: "F = k\\frac{q_1 q_2}{r^2} \\quad \\Rightarrow \\quad F_{new} = F_{old} \\times \\left(\\frac{r_{old}}{r_{new}}\\right)^2",
      calculation: "Current force: 0.5 N at distance r\nDouble distance (2r): F = 0.5 × (1/2)² = 0.125 N\nTriple distance (3r): F = 0.5 × (1/3)² = 0.056 N\nHalf distance (r/2): F = 0.5 × (2)² = 2.0 N",
      insights: [
        "Inverse-square law: 2× distance = 1/4 force, 3× distance = 1/9 force (very rapid decay)",
        "Same principle governs gravity, light intensity, and sound intensity",
        "Halving distance quadruples force - why touching charged objects causes strong shock",
        "At atomic scales, this force determines chemical bonding strength and molecular structure"
      ],
      conclusion: "Doubling distance reduces force from 0.5 N to 0.125 N (75% reduction). Use this principle to increase safety margins: 2× distance gives 4× safety factor."
    }
  },

  "capacitance": {
    calculatorType: "Capacitance Calculator",
    basicQuestion: "I need a 100μF capacitor for my power supply. What happens if I use 220μF instead? Will it damage my circuit?",
    aiResponse: {
      summary: "Higher capacitance provides more filtering but affects charging time and ripple. Let me analyze the tradeoffs.",
      calculation: "100μF vs 220μF:\nRipple voltage: 220μF reduces ripple by 55% (better filtering)\nCharging time: 220μF takes 2.2× longer to charge\nSize/cost: 220μF is ~2× size and cost of 100μF",
      insights: [
        "Higher capacitance = smoother DC output (lower ripple) but slower response to load changes",
        "Won't damage circuit unless voltage rating is exceeded (always use ≥1.5× supply voltage)",
        "Electrolytic caps have tolerance of ±20%, so 100μF is actually 80-120μF anyway",
        "Startup surge current increases with capacitance - may require inrush limiter >500μF"
      ],
      conclusion: "Using 220μF instead of 100μF is perfectly safe and provides 55% better filtering. Only downsides are increased size, cost, and charging time. Go for it unless space is constrained."
    }
  },

  "resistance": {
    calculatorType: "Resistance Calculator",
    basicQuestion: "I need 1kΩ but only have 1.2kΩ resistors. How do I combine them to get close to 1kΩ?",
    aiResponse: {
      summary: "Resistor combinations follow series (add) and parallel (reciprocal) rules. Let me find the best configuration.",
      formula: "R_{series} = R_1 + R_2 \\quad \\text{and} \\quad \\frac{1}{R_{parallel}} = \\frac{1}{R_1} + \\frac{1}{R_2}",
      calculation: "Target: 1000Ω\nOption 1: Two 1.2kΩ in series = 2.4kΩ (too high)\nOption 2: Two 1.2kΩ in parallel = 600Ω (too low)\nOption 3: Three 1.2kΩ in parallel = 400Ω then series with 1.2kΩ = 1600Ω",
      insights: [
        "Best practical solution: 5× 1.2kΩ resistors in parallel = 240Ω, then add 4× in series ≈ 1020Ω (2% error)",
        "Resistor tolerance (±5% typical) means 1000Ω target is actually 950-1050Ω range anyway",
        "For non-critical circuits: 1.2kΩ alone is 20% high but may work fine (test it first)",
        "Consider using a potentiometer (variable resistor) if precision is critical"
      ],
      conclusion: "Simplest approach: use three 1.2kΩ resistors in parallel (400Ω) + one in series = 1600Ω. If that's too high, buy a 1kΩ resistor (they cost $0.01 each) rather than complex combinations."
    }
  },

  "resistor-color": {
    calculatorType: "Resistor Color Code Calculator",
    basicQuestion: "I have Brown-Black-Red-Gold bands. Is this 1kΩ, and what's the tolerance? Why do colors matter?",
    aiResponse: {
      summary: "Color codes encode resistance value and tolerance in a standardized format. Let me decode your resistor.",
      calculation: "Brown-Black-Red-Gold:\nBrown (1) Black (0) Red (×100) = 10 × 100 = 1000Ω = 1kΩ\nGold band = ±5% tolerance (950Ω to 1050Ω actual value)\n4-band system (most common)",
      insights: [
        "4-band (±5-10%): digit-digit-multiplier-tolerance (most common, economy grade)",
        "5-band (±1-2%): digit-digit-digit-multiplier-tolerance (precision resistors)",
        "Tolerance matters: gold (±5%) fine for most uses, blue/violet (±0.5-1%) for precision circuits",
        "Resistor orientation: read from the end with clustered bands, tolerance band is typically set apart"
      ],
      conclusion: "Yes, this is 1kΩ ±5% (950-1050Ω actual). Gold tolerance is fine for general use. For precision (op-amps, sensors), upgrade to 1% resistors (brown/red/violet tolerance band)."
    }
  },

  "magnetic-force": {
    calculatorType: "Magnetic Force Calculator",
    basicQuestion: "I calculated 5N of magnetic force. How strong of a magnet do I need to lift a 500g object against gravity?",
    aiResponse: {
      summary: "Lifting requires force ≥ weight. Let me calculate required magnetic force and compare to your 5N result.",
      formula: "F_{gravity} = mg = 0.5\\text{kg} \\times 9.8\\text{m/s}^2 = 4.9\\text{N}",
      calculation: "Weight to lift: 4.9N\nYour magnetic force: 5N\nSafety margin: 5/4.9 = 1.02× (2% overhead - barely enough!)\nRecommended force: 7-10N (40-100% safety margin)",
      insights: [
        "Static friction requires 1.5-2× weight to overcome initial sticking (dynamic friction is less)",
        "Magnetic force drops with distance² - perfect contact gives max force, 1mm gap reduces by 50%+",
        "Neodymium N52 magnets provide ~100× force of ceramic magnets for same size",
        "Temperature reduces magnetic strength: -5% per 100°C for neodymium (use samarium-cobalt for high temp)"
      ],
      conclusion: "Your 5N is theoretically sufficient but use 7-10N magnet (40-100% margin) to ensure reliable lifting accounting for friction, air gap, and temperature variation. Use N42 or N52 neodymium for best size/strength ratio."
    }
  },

  "faradays-law": {
    calculatorType: "Faraday's Law Calculator",
    basicQuestion: "I'm inducing 5V by moving a magnet through a coil. How can I increase this to 12V for my DIY generator?",
    aiResponse: {
      summary: "Induced voltage depends on rate of flux change, coil turns, and magnetic field strength. Let me optimize your generator.",
      formula: "\\mathcal{E} = -N\\frac{d\\Phi_B}{dt} \\quad \\text{where} \\quad \\Phi_B = B \\cdot A",
      calculation: "Current output: 5V\nTo reach 12V (2.4× increase):\n- Double magnet speed: 10V (limited by mechanics)\n- Use 2.4× more coil turns: 12V (best option)\n- Stronger magnet (2.4×): 12V (expensive neodymium)",
      insights: [
        "Most practical: increase coil turns from 100 to 240 turns (2.4×) = 12V at same speed",
        "Thinner wire allows more turns in same space but increases resistance (power loss)",
        "Generator efficiency: mechanical power in = electrical power out + resistive losses (aim for 70-90%)",
        "Multiple magnets or multi-pole design increases frequency without faster rotation"
      ],
      conclusion: "Rewind coil with 2.4× more turns (e.g., 100→240 turns) using slightly thinner wire. This gets you 12V without mechanical changes. Alternative: use 2× stronger neodymium magnets + 20% more turns."
    }
  },

  "rlc-circuit": {
    calculatorType: "RLC Circuit Calculator",
    basicQuestion: "My RLC circuit resonates at 1kHz. If I double the capacitance, what's the new resonant frequency?",
    aiResponse: {
      summary: "Resonant frequency follows inverse-square-root relationship with capacitance. Doubling C doesn't halve frequency - let me show you.",
      formula: "f_0 = \\frac{1}{2\\pi\\sqrt{LC}} \\quad \\Rightarrow \\quad f_{new} = f_{old} \\times \\frac{1}{\\sqrt{2}}",
      calculation: "Original: 1 kHz at capacitance C\nDouble capacitance (2C): f = 1000Hz / √2 = 707Hz (not 500Hz!)\nQuadruple capacitance (4C): f = 1000Hz / 2 = 500Hz",
      insights: [
        "Square-root relationship: 2× capacitance = 0.707× frequency (not 0.5×)",
        "To halve frequency: increase L or C by 4× (not 2×)",
        "Resonant circuits filter specific frequencies - used in radios to tune stations",
        "Quality factor Q = f₀/(bandwidth): higher Q = sharper resonance, better selectivity"
      ],
      conclusion: "Doubling capacitance reduces resonant frequency to 707Hz (not 500Hz due to square-root relationship). To reach exactly 500Hz, increase capacitance by 4× instead."
    }
  },

  // Chemistry - Gas Laws (Batch 2)
  "ideal-gas-law": {
    calculatorType: "Ideal Gas Law Calculator",
    basicQuestion: "I have 1 mole of gas at 273K and 1 atm occupying 22.4L. If I heat it to 373K, what's the new volume at constant pressure?",
    aiResponse: {
      summary: "Charles's Law governs volume-temperature relationship at constant pressure. Let me calculate the expansion.",
      formula: "\\frac{V_1}{T_1} = \\frac{V_2}{T_2} \\quad \\Rightarrow \\quad V_2 = V_1 \\times \\frac{T_2}{T_1}",
      calculation: "Initial: 22.4L at 273K\nFinal: V = 22.4L × (373K/273K) = 30.6L\nVolume increase: 8.2L (37% expansion)\nThis is why hot air balloons rise!",
      insights: [
        "Gas expands 0.37% per Kelvin (1/273) at constant pressure - predictable and linear in absolute temp",
        "Real gases deviate from ideal behavior at high pressure (>10 atm) or low temp (<200K)",
        "Critical for safety: sealed containers can explode if heated (use pressure relief valves)",
        "Altitude compensation: air density decreases 12% per 1000m elevation due to temperature/pressure changes"
      ],
      conclusion: "Heating from 273K to 373K increases volume to 30.6L (37% expansion). Never heat sealed rigid containers - use flexible containers or pressure relief systems for safety."
    }
  },

  "boyles-law": {
    calculatorType: "Boyle's Law Calculator",
    basicQuestion: "My scuba tank is 12L at 200 bar. How much breathing air does this provide at sea level (1 bar)?",
    aiResponse: {
      summary: "Boyle's Law tells us pressure and volume are inversely proportional. Your compressed tank holds much more usable air than it appears.",
      formula: "P_1V_1 = P_2V_2 \\quad \\Rightarrow \\quad V_2 = \\frac{P_1V_1}{P_2}",
      calculation: "Tank: 12L at 200 bar\nAt sea level (1 bar): V = (200 × 12) / 1 = 2400L of breathable air\nBreathing rate: ~25L/min at rest, 50L/min swimming\nDive time: 2400L / 50L/min = 48 minutes of swimming",
      insights: [
        "200 bar compression stores 200× the air in same volume - why scuba tanks are heavy but last long",
        "Depth matters: at 10m depth (2 bar pressure), air consumption doubles vs. surface",
        "Always reserve 50 bar (600L) for safety ascent - never drain tank completely",
        "Cold water increases breathing rate 25-50% due to body working harder to stay warm"
      ],
      conclusion: "Your 12L tank at 200 bar provides 2400L of breathable air. At 50L/min swim rate, expect 40-45 minute dive (accounting for 50 bar reserve). Plan shallower for longer dives."
    }
  },

  "charles-law": {
    calculatorType: "Charles's Law Calculator",
    basicQuestion: "My car tire pressure is 32 PSI at 20°C. What will it be at 40°C on a hot day? Is this dangerous?",
    aiResponse: {
      summary: "Temperature affects tire pressure linearly (in absolute temp). Let me calculate the hot-weather pressure increase.",
      formula: "\\frac{P_1}{T_1} = \\frac{P_2}{T_2} \\quad \\Rightarrow \\quad P_2 = P_1 \\times \\frac{T_2}{T_1}",
      calculation: "Initial: 32 PSI at 293K (20°C)\nHot day: P = 32 × (313K/293K) = 34.2 PSI\nIncrease: 2.2 PSI (7% rise)\nRecommended range: 30-35 PSI (you're within spec)",
      insights: [
        "Tire pressure increases ~1 PSI per 10°F (5.6°C) temperature rise - completely normal",
        "Blowout risk at 45+ PSI for standard tires (you need 130°C to reach this from 32 PSI)",
        "Under-inflation (< 28 PSI) is more dangerous than over-inflation - causes sidewall failure",
        "Check pressure when 'cold' (morning before driving) for accurate baseline reading"
      ],
      conclusion: "34.2 PSI at 40°C is perfectly safe (still below 35 PSI max cold rating). Don't release air when hot - you'll be under-inflated when cool. Check tire specs for max cold pressure (usually 35-44 PSI)."
    }
  },

  "combined-gas-law": {
    calculatorType: "Combined Gas Law Calculator",
    basicQuestion: "I'm moving from Denver (1 bar, 25°C) to San Francisco (1 bar, 15°C). How does this affect my car's gas tank pressure?",
    aiResponse: {
      summary: "Both pressure and temperature change when relocating. Combined Gas Law accounts for both effects simultaneously.",
      formula: "\\frac{P_1V_1}{T_1} = \\frac{P_2V_2}{T_2} \\quad \\text{(assuming rigid tank: } V_1=V_2\\text{)}",
      calculation: "Denver: P=1 bar, T=298K (25°C)\nSan Francisco: P=1 bar, T=288K (15°C)\nPressure change: ΔP = 1 × (288/298) = 0.966 bar\nDecrease: 3.4% (minimal impact)",
      insights: [
        "Rigid fuel tank means volume is constant - only temperature affects internal pressure",
        "3.4% pressure drop is negligible for car systems (fuel pumps handle 0.5-3 bar range)",
        "Altitude change matters more: 1000m elevation = 11% pressure drop (affects engine tuning)",
        "Cold weather reduces vapor pressure - why cars are harder to start in winter"
      ],
      conclusion: "Temperature drop from 25°C to 15°C causes only 3.4% pressure decrease - no practical impact on your car. Altitude differences (Denver 1600m vs SF 50m) have bigger effects on engine performance."
    }
  },

  "van-der-waals": {
    calculatorType: "Van der Waals Calculator",
    basicQuestion: "Why does ideal gas law fail at high pressure? When should I use Van der Waals equation instead?",
    aiResponse: {
      summary: "Ideal gas law assumes point particles with no interactions. Van der Waals corrects for molecular size and intermolecular forces at extreme conditions.",
      formula: "\\left(P + \\frac{an^2}{V^2}\\right)(V - nb) = nRT",
      calculation: "CO₂ at 100 bar:\nIdeal Gas Law: V = 0.245 L/mol (off by 15%)\nVan der Waals: V = 0.289 L/mol (accurate)\nDeviation: 18% error in ideal gas prediction",
      insights: [
        "Use ideal gas law when: P < 10 bar, T > 200K, non-polar gases (O₂, N₂, He)",
        "Use Van der Waals when: P > 10 bar, T < 200K, or near critical point (CO₂, NH₃)",
        "Correction factors: 'a' accounts for attractive forces, 'b' accounts for molecular volume",
        "Real-world applications: refrigeration cycles, supercritical CO₂ extraction, industrial gas storage"
      ],
      conclusion: "Switch to Van der Waals equation when pressure exceeds 10 bar or temperature drops below 200K. For everyday calculations (room temp, low pressure), ideal gas law is accurate within 2-5%."
    }
  },

  // Chemistry - Thermodynamics (Batch 2)
  "heat-transfer": {
    calculatorType: "Heat Transfer Calculator",
    basicQuestion: "I calculated 5000J of heat transfer. How long will this take to heat 1L of water by 10°C, and what power heater do I need?",
    aiResponse: {
      summary: "Heat transfer rate determines heating time. Let me calculate power requirements and time for your water heating scenario.",
      formula: "Q = mc\\Delta T = 1000g \\times 4.18J/(g\\cdot°C) \\times 10°C = 41,800J",
      calculation: "Heat needed: 41,800J (not 5000J - need 8.4× more)\nWith 1000W heater: t = 41,800J / 1000W = 42 seconds\nWith 100W heater: t = 418 seconds = 7 minutes\nYour 5000J: heats water by 1.2°C only",
      insights: [
        "Water has high specific heat (4.18 J/g°C) - requires lots of energy to heat",
        "Practical kettle (1500W): boils 1L water (80°C rise) in ~4 minutes",
        "Heat loss matters: insulated container heats 30% faster than open container",
        "Microwave efficiency: 50-60% vs. stovetop 70-75% vs. electric kettle 85%+"
      ],
      conclusion: "To heat 1L water by 10°C in 1 minute, you need 700W heater (accounting for heat loss). Your 5000J only provides enough energy for 1.2°C rise - need 42kJ total."
    }
  },

  "enthalpy-change": {
    calculatorType: "Enthalpy Change Calculator",
    basicQuestion: "My reaction's ΔH is -50 kJ/mol. Is this a lot of heat? How does it compare to burning gasoline?",
    aiResponse: {
      summary: "-50 kJ/mol is moderately exothermic. Let me benchmark this against common energy sources and explain the implications.",
      calculation: "Your reaction: -50 kJ/mol\nGasoline combustion: -5500 kJ/mol (110× more energetic)\nGlucose metabolism: -2800 kJ/mol (56× more)\nNeutral-ization (acid+base): -55 kJ/mol (similar to yours)",
      insights: [
        "Negative ΔH = exothermic (releases heat), positive ΔH = endothermic (absorbs heat)",
        "-50 kJ/mol is similar to acid-base neutralization - noticeable warmth but not dangerous",
        "For scale: 1 mol releasing 50 kJ heats 1L water from 20°C to 32°C",
        "Industrial importance: large ΔH reactions (combustion, polymerization) require careful heat management"
      ],
      conclusion: "-50 kJ/mol is moderately exothermic - you'll feel warmth but it's safe for lab-scale reactions. It's 2% as energetic as gasoline, comparable to common acid-base reactions."
    }
  },

  "entropy-change": {
    calculatorType: "Entropy Change Calculator",
    basicQuestion: "My reaction has ΔS = +150 J/(mol·K). What does positive entropy mean, and does this favor the reaction?",
    aiResponse: {
      summary: "Positive entropy means increased disorder/randomness. Combined with temperature and enthalpy, it determines if reaction is spontaneous via Gibbs free energy.",
      formula: "\\Delta G = \\Delta H - T\\Delta S \\quad \\text{(spontaneous if } \\Delta G < 0\\text{)}",
      calculation: "ΔS = +150 J/(mol·K)\nAt 298K: -TΔS = -44.7 kJ/mol (favors reaction)\nIf ΔH = -20 kJ/mol: ΔG = -20 - 44.7 = -64.7 kJ/mol (very spontaneous!)\nIf ΔH = +60 kJ/mol: ΔG = +60 - 44.7 = +15.3 kJ/mol (non-spontaneous)",
      insights: [
        "Positive ΔS (increasing disorder) favors spontaneity - gases from solids, mixing, dissociation",
        "Temperature amplifies entropy: higher T makes ΔS more important in determining spontaneity",
        "Classic example: ice melting (ΔS > 0) is favored above 273K, disfavored below",
        "Phase changes: solid→liquid→gas always have ΔS > 0 (increasing freedom of motion)"
      ],
      conclusion: "+150 J/(mol·K) strongly favors spontaneity via entropy term (-45 kJ/mol at room temp). If ΔH is also negative, reaction is very spontaneous. If ΔH is positive, need ΔH < 45 kJ/mol for spontaneity at 298K."
    }
  },

  "gibbs-free-energy": {
    calculatorType: "Gibbs Free Energy Calculator",
    basicQuestion: "My ΔG is -25 kJ/mol at 25°C. Is this reaction fast, and will it go to completion?",
    aiResponse: {
      summary: "ΔG determines direction and equilibrium, NOT reaction rate. Let me explain the critical distinction between thermodynamics and kinetics.",
      formula: "\\Delta G = -RT\\ln(K_{eq}) \\quad \\Rightarrow \\quad K_{eq} = e^{-\\Delta G / RT}",
      calculation: "ΔG = -25 kJ/mol at 298K\nK_eq = e^(25000/2478) = e^10.1 = 24,000\nEquilibrium: 24,000:1 product:reactant ratio (essentially complete conversion)\nRate: Cannot determine from ΔG alone! Could be seconds or centuries.",
      insights: [
        "ΔG < 0 means thermodynamically favorable (will happen eventually), NOT fast",
        "Diamond→graphite: ΔG = -2.9 kJ/mol (favored) but takes billions of years (kinetically slow)",
        "Activation energy Ea determines rate: add catalyst to lower Ea without changing ΔG",
        "K_eq > 1000 means >99.9% conversion at equilibrium (your ΔG gives K_eq = 24,000)"
      ],
      conclusion: "ΔG = -25 kJ/mol guarantees reaction will reach 99.9%+ product at equilibrium (K_eq = 24,000). But speed is unknown - check activation energy and add catalyst if reaction is too slow."
    }
  },

  "activation-energy": {
    calculatorType: "Activation Energy Calculator",
    basicQuestion: "My reaction has Ea = 80 kJ/mol. If I increase temperature from 25°C to 50°C, how much faster will it go?",
    aiResponse: {
      summary: "Arrhenius equation shows exponential rate increase with temperature. Let me calculate the speedup for your 25°C increase.",
      formula: "\\frac{k_2}{k_1} = e^{-E_a/R \\left(\\frac{1}{T_2} - \\frac{1}{T_1}\\right)}",
      calculation: "Ea = 80 kJ/mol\nT₁ = 298K (25°C), T₂ = 323K (50°C)\nRate ratio: k₂/k₁ = e^(80000/8.314 × (1/298 - 1/323)) = e^2.5 = 12.2×\nReaction is 12× faster at 50°C!",
      insights: [
        "Rule of thumb: reaction rate doubles every 10°C increase (valid for Ea ≈ 50-75 kJ/mol)",
        "Your Ea = 80 kJ/mol: rate increases 2.5× per 10°C (higher than typical)",
        "Catalysts lower Ea without changing equilibrium - a 20 kJ/mol reduction = 2200× speedup at 25°C",
        "Industrial optimization: 10°C increase gives 2× rate but costs 5-10% more energy input"
      ],
      conclusion: "Increasing temperature from 25°C to 50°C makes your reaction 12× faster. But consider energy costs: catalyst that reduces Ea by 20 kJ/mol achieves 2200× speedup without heating."
    }
  },

  "carnot-efficiency": {
    calculatorType: "Carnot Efficiency Calculator",
    basicQuestion: "My heat engine operates between 600K and 300K with 50% Carnot efficiency. Can I improve this to 70%?",
    aiResponse: {
      summary: "Carnot efficiency is a theoretical maximum set by thermodynamics. Let me show you the limits and practical considerations.",
      formula: "\\eta_{Carnot} = 1 - \\frac{T_C}{T_H} = 1 - \\frac{300K}{600K} = 50\\%",
      calculation: "Your setup: 50% Carnot efficiency (matches maximum!)\nTo reach 70% Carnot: need T_C = 180K (-93°C) OR T_H = 1000K (727°C)\nReal engines: 40-45% efficiency (80-90% of Carnot maximum)\n70% is thermodynamically impossible with your temps!",
      insights: [
        "Carnot efficiency is theoretical maximum - no real engine can exceed it (2nd law of thermodynamics)",
        "Real engines: friction, heat loss, non-ideal cycles reduce efficiency to 70-90% of Carnot",
        "Power plants: 33% actual vs. 65% Carnot (51% of theoretical max)",
        "To improve: increase T_H (limited by materials) or decrease T_C (limited by environment)"
      ],
      conclusion: "50% is already the Carnot maximum for 600K/300K. You cannot reach 70% with these temperatures - it violates thermodynamics. To improve: superheat steam to 800K (60% Carnot) or add combined-cycle (45% real efficiency)."
    }
  },

  "thermal-expansion": {
    calculatorType: "Thermal Expansion Calculator",
    basicQuestion: "A 10m steel beam expands by 1.2mm when heated 10°C. Do I need expansion joints in my construction project?",
    aiResponse: {
      summary: "Thermal expansion creates significant stress in constrained structures. Let me calculate forces and recommend expansion joint spacing.",
      formula: "\\Delta L = \\alpha L \\Delta T \\quad \\text{where} \\quad \\alpha_{steel} = 12 \\times 10^{-6} / °C",
      calculation: "Your beam: 10m, ΔT = 10°C, ΔL = 1.2mm\nSummer heat (40°C swing): ΔL = 4.8mm expansion\nStress if constrained: σ = Eα ΔT = 200 GPa × 12×10⁻⁶ × 40°C = 96 MPa\nYield stress of steel: ~250 MPa (you're at 38% of failure!)",
      insights: [
        "Always use expansion joints for structures >15m or temperature swings >20°C",
        "Joint spacing: every 30-40m for steel/concrete, 20-25m for bridges",
        "Coefficient matters: aluminum expands 2× steel, PVC expands 5× steel",
        "Failure modes: buckling (compression), cracking (tension), bearing failure at supports"
      ],
      conclusion: "Yes, you need expansion joints! Space them every 30m maximum for steel structures, 20m for bridges. Your 1.2mm/10°C rate means 5mm movement in 40°C swing - ignoring this causes buckling or cracking."
    }
  }
};
